import { useEffect, useState } from 'react';
import { CATALOGUE } from '../data/catalogue';
import { CLEARANCE, WARRANTY_TEXT } from '../data/clearance';
import { SERIES } from '../data/highlights';
import { SPECS } from '../data/specs';
import * as brands from '../data/brands';
import { calcEnergy, KW_PER_TON } from '../pages/ac-engine';
import { DELIVERY, PRICES, STOCK, RATES, KIT_COPPER_METRES } from '../data/pricing';
import { MAX_COMPARE, buildVariants } from '../data/compare';
import { onlineKit } from './listing';

// The compare tray: which ACs the visitor has picked. Kept in localStorage
// (wrapped in try/catch, so it works when storage is blocked) and announced
// with an event, the same way lib/cart.js does.

const KEY = 'cc_compare_v1';
const EVENT = 'cc_compare_change';

// Everything data/compare.js needs from the rest of the app, in one place.
export const COMPARE_CTX = {
  SERIES, SPECS, brands, calcEnergy, KW_PER_TON,
  deliveryBadge: DELIVERY.badge,
  kitCopper: { rate: RATES.copperSplit.price, metres: KIT_COPPER_METRES },
  clearanceWarranty: WARRANTY_TEXT,
};

// Every unit that can go on the table: each Daikin model with and without
// the pipe kit, and the clearance units in stock. Prices and stock from
// pricing.js only.
export const compareVariants = () => buildVariants({ CATALOGUE, PRICES, STOCK, onlineKit, CLEARANCE });
const VALID = new Set(compareVariants().map((v) => v.id));

// Tray entries are unit ids: "gtkl-50", "gtkl-50~kit", "cl-ras-e518pccib1".
export const cleanIds = (ids) =>
  [...new Set((ids || []).map(String).filter((id) => VALID.has(id)))].slice(0, MAX_COMPARE);

export function readTray() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return cleanIds(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
}

export function writeTray(ids) {
  const clean = cleanIds(ids);
  try {
    localStorage.setItem(KEY, JSON.stringify(clean));
  } catch {
    /* storage blocked: the tray lives in memory for this page only */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENT, { detail: clean }));
  return clean;
}

export const toggleTray = (id) => {
  const ids = readTray();
  if (ids.includes(id)) return writeTray(ids.filter((x) => x !== id));
  if (ids.length >= MAX_COMPARE) return ids;
  return writeTray([...ids, id]);
};

export const clearTray = () => writeTray([]);

export function subscribeTray(fn) {
  if (typeof window === 'undefined') return () => {};
  const onChange = () => fn(readTray());
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

export function useTray() {
  const [ids, setIds] = useState(() => readTray());
  useEffect(() => subscribeTray(setIds), []);
  return ids;
}

export const compareHref = (ids) => `/compare-ac?ac=${cleanIds(ids).join(',')}`;
